import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, User, Mail, Phone, Lock, Save, X, Eye, EyeOff } from 'lucide-react';
import superAdminService from '../../services/superadmin/superAdminService';
import toast from 'react-hot-toast';

const AddAdmin: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    password: '',
    mobile_number: '',
    confirmPassword: '',
  });

  const [errors, setErrors] = useState({
    full_name: '',
    email: '',
    password: '',
    mobile_number: '',
    confirmPassword: '',
  });

  const validateForm = () => {
    let isValid = true;
    const newErrors = {
      full_name: '',
      email: '',
      password: '',
      mobile_number: '',
      confirmPassword: '',
    };

    // Validate full name
    if (!formData.full_name.trim()) {
      newErrors.full_name = 'Full name is required';
      isValid = false;
    }

    // Validate email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
      isValid = false;
    }

    // Validate password
    if (!formData.password) {
      newErrors.password = 'Password is required';
      isValid = false;
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
      isValid = false;
    }

    // Validate confirm password
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm password';
      isValid = false;
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
      isValid = false;
    }

    // Validate mobile number (optional but if provided, should be valid)
    if (formData.mobile_number && !/^[0-9+\-\s()]+$/.test(formData.mobile_number)) {
      newErrors.mobile_number = 'Please enter a valid mobile number';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Please fix all errors');
      return;
    }

    try {
      setLoading(true);
      
      await superAdminService.createAdmin({
        full_name: formData.full_name,
        email: formData.email,
        password: formData.password,
        mobile_number: formData.mobile_number,
      });
      
      toast.success('Admin created successfully!');
      navigate('/super-admin/admin-management');
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Failed to create admin');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData({ ...formData, [field]: value });
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigate('/super-admin/admin-management')}
            className="bg-secondary-100 hover:bg-secondary-200 p-2 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-secondary-700" />
          </button>
          <div>
            <h1 className="text-3xl font-bold text-secondary-900 mb-2">Add New Admin</h1>
            <p className="text-secondary-600">Create a new admin account</p>
          </div>
        </div>
      </div>

      {/* Form Card */}
      <div className="bg-white rounded-xl shadow-soft p-8 max-w-3xl">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Full Name */}
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-2">
              Full Name <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => handleChange('full_name', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                  errors.full_name 
                    ? 'border-danger-500 focus:border-danger-600' 
                    : 'border-secondary-200 focus:border-primary-500'
                }`}
                placeholder="Enter full name"
              />
            </div>
            {errors.full_name && (
              <p className="text-danger-500 text-sm mt-1">{errors.full_name}</p>
            )}
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-2">
              Email Address <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                  errors.email 
                    ? 'border-danger-500 focus:border-danger-600' 
                    : 'border-secondary-200 focus:border-primary-500'
                }`}
                placeholder="admin@rita.com"
              />
            </div>
            {errors.email && (
              <p className="text-danger-500 text-sm mt-1">{errors.email}</p>
            )}
          </div>

          {/* Mobile Number */}
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-2">
              Mobile Number
            </label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
              <input
                type="tel"
                value={formData.mobile_number}
                onChange={(e) => handleChange('mobile_number', e.target.value)}
                className={`w-full pl-10 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                  errors.mobile_number 
                    ? 'border-danger-500 focus:border-danger-600' 
                    : 'border-secondary-200 focus:border-primary-500'
                }`}
                placeholder="+91 98765 43210"
              />
            </div>
            {errors.mobile_number && (
              <p className="text-danger-500 text-sm mt-1">{errors.mobile_number}</p>
            )}
          </div>

          {/* Password */}
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-2">
              Password <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={formData.password}
                onChange={(e) => handleChange('password', e.target.value)}
                className={`w-full pl-10 pr-12 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                  errors.password 
                    ? 'border-danger-500 focus:border-danger-600' 
                    : 'border-secondary-200 focus:border-primary-500'
                }`}
                placeholder="Minimum 8 characters"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <p className="text-danger-500 text-sm mt-1">{errors.password}</p>
            )}
          </div>

          {/* Confirm Password */}
          <div>
            <label className="block text-sm font-semibold text-secondary-700 mb-2">
              Confirm Password <span className="text-danger-500">*</span>
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-secondary-400" size={20} />
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={formData.confirmPassword}
                onChange={(e) => handleChange('confirmPassword', e.target.value)}
                className={`w-full pl-10 pr-12 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                  errors.confirmPassword 
                    ? 'border-danger-500 focus:border-danger-600' 
                    : 'border-secondary-200 focus:border-primary-500'
                }`}
                placeholder="Re-enter password"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-secondary-400 hover:text-secondary-600 transition-colors"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="text-danger-500 text-sm mt-1">{errors.confirmPassword}</p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-4">
            <button
              type="submit"
              disabled={loading}
              className="flex-1 bg-primary-500 hover:bg-primary-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save size={20} />
              <span>{loading ? 'Creating...' : 'Create Admin'}</span>
            </button>
            <button
              type="button"
              onClick={() => navigate('/super-admin/admin-management')}
              className="flex-1 bg-secondary-200 hover:bg-secondary-300 text-secondary-900 py-3 rounded-lg font-semibold flex items-center justify-center space-x-2 transition-all"
            >
              <X size={20} />
              <span>Cancel</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddAdmin;

